import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Assignment as ProjectIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useLoading } from '../../contexts/LoadingContext';
import { useNotify } from '../ui/NotificationSystem';
import { useProjectService, Project as ProjectType } from '../../services/ProjectService';

// Use the Project type from ProjectService
type Project = ProjectType;

type AssignableUser = {
  id: number;
  name: string;
  email: string;
};

interface ProjectStats {
  total: number;
  active: number;
  completed: number;
  overdue: number;
  totalBudget: number;
  totalSpent: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-tabpanel-${index}`}
      aria-labelledby={`project-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ProjectManagement: React.FC = () => {
  // Initialize the project service hook
  const projectService = useProjectService();
  const navigate = useNavigate();
  
  const [projects, setProjects] = useState<Project[]>([]);
  const [stats, setStats] = useState<ProjectStats | null>(null);
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client_id: 0,
    project_manager_id: undefined as number | undefined,
    status: 'draft',
    priority: 'normal',
    budget_range: '',
    expected_start_date: '',
    expected_end_date: ''
  });

  const { setLoading: setGlobalLoading } = useLoading();
  const { success: notifySuccess, error: notifyError } = useNotify();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load projects, stats, and assignable users in parallel
      const [projectsArray, statsData, assignableUsersData] = await Promise.all([
        projectService.getProjects(),
        projectService.getProjectStatistics(),
        projectService.getAssignableUsers()
      ]);

      setProjects(projectsArray);
      
      // Transform project stats for the interface
      const transformedStats: ProjectStats = {
        total: statsData.total_projects || 0,
        active: statsData.active_projects || 0,
        completed: statsData.completed_projects || 0,
        overdue: statsData.needs_review || 0, // Use needs_review as proxy for overdue
        totalBudget: 0, // Will need to calculate from projects
        totalSpent: 0   // Will need to calculate from projects
      };
      setStats(transformedStats);
      
      // Use the project managers from the assignable users endpoint
      setUsers(assignableUsersData.projectManagers);
      
    } catch (err: any) {
      console.error('Error loading project data:', err);
      setError('Failed to load project data. Please try again.');
      notifyError('Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    try {
      setGlobalLoading('projects', true);
      
      const projectData = {
        ...formData,
        project_types: ['web_development'], // Default for now
        technical_requirements: {},
        business_requirements: {}
      };
      
      const newProject = await projectService.createProject(projectData);
      setProjects(prev => [newProject, ...prev]);
      setOpenDialog(false);
      resetForm();
      notifySuccess('Project created successfully!');
      
    } catch (err: any) {
      console.error('Error creating project:', err);
      notifyError(err.message || 'Failed to create project');
    } finally {
      setGlobalLoading('projects', false);
    }
  };

  const handleUpdateProject = async () => {
    if (!selectedProject) return;
    
    try {
      setGlobalLoading('projects', true);
      
      const updatedProject = await projectService.updateProject(selectedProject.id, formData);
      setProjects(prev => prev.map(p => p.id === selectedProject.id ? updatedProject : p));
      setOpenDialog(false);
      setSelectedProject(null);
      resetForm();
      notifySuccess('Project updated successfully!');
      
    } catch (err: any) {
      console.error('Error updating project:', err);
      notifyError(err.message || 'Failed to update project');
    } finally {
      setGlobalLoading('projects', false);
    }
  };

  const handleDeleteProject = async (project: Project) => {
    if (!window.confirm(`Are you sure you want to delete "${project.name}"?`)) {
      return;
    }
    
    try {
      setGlobalLoading('projects', true);
      await projectService.deleteProject(project.id);
      setProjects(prev => prev.filter(p => p.id !== project.id));
      notifySuccess('Project deleted successfully!');
      
    } catch (err: any) {
      console.error('Error deleting project:', err);
      notifyError(err.message || 'Failed to delete project');
    } finally {
      setGlobalLoading('projects', false);
    }
  };

  const handleViewProject = (project: Project) => {
    navigate(`/dashboard/admin/projects/${project.id}`);
  };

  const openCreateDialog = () => {
    setIsEditMode(false);
    setSelectedProject(null);
    resetForm();
    setOpenDialog(true);
  };

  const openEditDialog = (project: Project) => {
    setIsEditMode(true);
    setSelectedProject(project);
    setFormData({
      name: project.name,
      description: project.description,
      client_id: project.client_id,
      project_manager_id: project.project_manager_id,
      status: project.status,
      priority: project.priority,
      budget_range: project.budget_range || '',
      expected_start_date: project.expected_start_date || '',
      expected_end_date: project.expected_end_date || ''
    });
    setOpenDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      client_id: 0,
      project_manager_id: undefined,
      status: 'draft',
      priority: 'normal',
      budget_range: '',
      expected_start_date: '',
      expected_end_date: ''
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'primary';
      case 'approved': return 'info';
      case 'cancelled': return 'error';
      case 'draft': return 'default';
      default: return 'warning';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'normal': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  if (loading && projects.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading projects...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Project Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          Create Project
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Box display="flex" gap={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
          <Card sx={{ minWidth: 200 }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ProjectIcon color="primary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.total}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Projects
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 200 }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.active}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Active
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 200 }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="info" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.completed}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Completed
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Projects Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Project Name</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Project Manager</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.map((project) => (
                  <TableRow key={project.id} hover>
                    <TableCell>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          color: 'primary.main',
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                        onClick={() => handleViewProject(project)}
                      >
                        {project.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {project.description?.substring(0, 50)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {project.client?.name || 'No client assigned'}
                    </TableCell>
                    <TableCell>
                      {project.project_manager?.name || 'Unassigned'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={project.status.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(project.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={(project.priority || 'normal').toUpperCase()}
                        color={getPriorityColor(project.priority || 'normal') as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LinearProgress
                          variant="determinate"
                          value={project.completion_percentage}
                          sx={{ width: 60, height: 6 }}
                        />
                        <Typography variant="body2">
                          {project.completion_percentage}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleViewProject(project)}
                        title="View Project"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(project)}
                        title="Edit Project"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteProject(project)}
                        color="error"
                        title="Delete Project"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {projects.length === 0 && (
            <Box textAlign="center" py={4}>
              <ProjectIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                No projects found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Get started by creating your first project
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Project Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditMode ? 'Edit Project' : 'Create New Project'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Project Name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Project Manager</InputLabel>
                <Select
                  value={formData.project_manager_id || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    project_manager_id: e.target.value ? Number(e.target.value) : undefined 
                  }))}
                >
                  <MenuItem value="">
                    <em>Select Project Manager</em>
                  </MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="submitted">Submitted</MenuItem>
                  <MenuItem value="in_review">In Review</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Budget Range"
                value={formData.budget_range}
                onChange={(e) => setFormData(prev => ({ ...prev, budget_range: e.target.value }))}
                placeholder="e.g., $10,000 - $50,000"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                type="date"
                label="Expected Start Date"
                value={formData.expected_start_date}
                onChange={(e) => setFormData(prev => ({ ...prev, expected_start_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                type="date"
                label="Expected End Date"
                value={formData.expected_end_date}
                onChange={(e) => setFormData(prev => ({ ...prev, expected_end_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={isEditMode ? handleUpdateProject : handleCreateProject}
            disabled={!formData.name.trim()}
          >
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectManagement;